import { computed, provide, inject, Ref } from "vue";
import MarketSupports from "../lib/marketSupports";
import {
  HANDICAP_BETTING,
  MATCH_WINNER,
  OVER_UNDER_TOTALS,
  FTR_GROUP,
  WW_GROUP,
  WDW_GROUP,
  LIST_GROUP,
  PROMO_GROUP,
  MAIN_MARKET_GROUP,
  FULL_TIME_RESULT,
  INSIGHT_GROUP
} from "../constants/market-types";
import { BetData, Market } from "@fresh8/types/src/betData";
import { SportEvent } from "@fresh8/types/src/betData";
import { CardTypes } from "@fresh8/types/src";
interface EventMarketGroups {
  [PROMO_GROUP]?: string[];
  [FTR_GROUP]?: string[];
  [LIST_GROUP]?: string[];
  [MAIN_MARKET_GROUP]?: string[];
  [INSIGHT_GROUP]?: string[];
  [WDW_GROUP]?: string[];
  [WW_GROUP]?: string[];
}

const MAIN_MARKET_SPORTS = [
  "Basketball",
  "Baseball",
  "American Football",
  "Ice Hockey"
];

const marketSupports = MarketSupports();
type Markets = Record<string, Market>;

/**
 * Method to group all the marketIds for an event by their normalised market type.
 * @param {string[]} marketIds
 */
const getMarketsGroupedByType = (
  marketIds: string[],
  markets: Markets
): EventMarketGroups => {
  return marketIds.reduce((groupAccumulator, currentID) => {
    const marketType = markets[currentID].marketType;
    const isPriorityMarket = markets[currentID].priorityMarket;
    const marketTypeGroup = marketSupports[marketType];

    let accumulatorGroup: string[] = [];
    if (groupAccumulator[marketTypeGroup]) {
      // make sure the priority market is always first in the marketTypeGroup
      if (isPriorityMarket) {
        accumulatorGroup = [currentID, ...groupAccumulator[marketTypeGroup]];
      } else {
        accumulatorGroup = [...groupAccumulator[marketTypeGroup], currentID];
      }
    } else {
      accumulatorGroup = [currentID];
    }

    return {
      ...groupAccumulator,
      [marketTypeGroup]: accumulatorGroup
    };
  }, {});
};

/**
 * method to pull our the market ids that only match the main market types from the FTR market type group.
 * @param {string[]} ftrMarkets
 */
const getMainMarkets = (
  ftrMarkets: string[] | undefined,
  markets: Markets,
  sport: string
): string[] => {
  if (!ftrMarkets || !MAIN_MARKET_SPORTS.includes(sport)) {
    return [];
  }

  const mainMarkets = ftrMarkets.reduce(
    (mainMarketAccumulator, currentID) => {
      const currentMarketType = markets[currentID].marketType;
      // only map current market type id if an id hasn't been mapped already for that market type
      if (mainMarketAccumulator[currentMarketType] === "") {
        return {
          ...mainMarketAccumulator,
          [currentMarketType]: currentID
        };
      }
      return mainMarketAccumulator;
    },
    {
      [HANDICAP_BETTING]: "",
      [MATCH_WINNER]: "",
      [OVER_UNDER_TOTALS]: ""
    }
  );
  // removing markets that haven't got ids.
  return Object.values(mainMarkets).filter(market => market !== "");
};

const generateParlayReducer = (markets: Record<string, Market>) => {
  return (accumulator: string[], currentValue: SportEvent) => {
    const matchedMarket = currentValue.markets.find(marketId => {
      const currentMarketType = markets[marketId].marketType;

      return currentMarketType === FULL_TIME_RESULT;
    });

    if (matchedMarket) {
      accumulator = [...accumulator, matchedMarket];
    }

    return accumulator;
  };
};

/**
 * Checks if any of the markets have any insights
 * if there are market insights MarketInsight and MarketInsightScore will no longer be optional
 * in current scope
 */

function hasInsights(markets: Market[]): markets is MarketWithInsight[] {
  return markets.some(market => {
    if (!market.marketInsight) false;
    return market.marketInsight && market.marketInsight.length > 0;
  });
}

type MarketWithInsight = Market & {
  marketInsightScore: number;
  marketInsight: string;
};

/**
 * reduce down to market which contains the highest scoring insight
 */

function insightReducer(
  highest: MarketWithInsight,
  cur: MarketWithInsight
): MarketWithInsight {
  return cur.marketInsightScore > highest.marketInsightScore ? cur : highest;
}

/**
 * Find the market with the highest scoring insight,
 * if market has no insight return undefined (doesnt render insight card)
 */

function findMarketInsight(markets: Market[]): string[] | undefined {
  if (hasInsights(markets)) {
    // reduce markets down to the single market that has the highest marketInsightScore
    const marketInsight = markets.reduce(insightReducer);

    // threshold for 'relevent' insight is a score of 60, if heightest scoring insight is below threshold do not render insight
    if (marketInsight.marketInsightScore < 60) return undefined;

    return [marketInsight.marketId];
  }
  return undefined;
}

const hasDrawOption = (eventMarket: Market) => {
  return eventMarket.options.length > 2;
};

function useDisplayEvents(betData: Ref<BetData>) {
  const parlayMarkets = computed(() => {
    return (
      betData.value?.events &&
      betData.value?.markets &&
      Object.values(betData.value.events)
        .reduce(generateParlayReducer(betData.value.markets), [])
        .slice(0, 3)
    );
  });

  /**
   * Mapping the event markets into their groups first to reduce repeated
   * operations of mapping markets.
   */
  const eventsWithGroupedMarkets = computed(() => {
    return (
      betData.value?.events &&
      Object.values(betData.value?.events).map(evnt => {
        // group event markets by card type
        const marketTypeGroups = getMarketsGroupedByType(
          evnt.markets,
          betData.value?.markets
        );

        // use only list market
        marketTypeGroups[LIST_GROUP]?.splice(1);
        marketTypeGroups[PROMO_GROUP]?.splice(1);

        // collate only the main market types
        const mainMarkets =
          marketTypeGroups[FTR_GROUP] &&
          getMainMarkets(
            marketTypeGroups[FTR_GROUP],
            betData.value?.markets,
            evnt.sport
          );
        // Display Main market card if more than 1 main market type exists.
        if (mainMarkets && mainMarkets.length > 1) {
          marketTypeGroups[MAIN_MARKET_GROUP] = mainMarkets;

          marketTypeGroups[INSIGHT_GROUP] = findMarketInsight(
            mainMarkets.map(marketId => betData.value.markets[marketId])
          );

          marketTypeGroups[FTR_GROUP] = undefined;
        } else {
          marketTypeGroups[FTR_GROUP]?.splice(1);
          const ftrMarketId = marketTypeGroups[FTR_GROUP]?.[0];

          if (
            ftrMarketId &&
            hasDrawOption(betData.value?.markets[ftrMarketId])
          ) {
            marketTypeGroups[WDW_GROUP] = [ftrMarketId];
          } else if (ftrMarketId) {
            marketTypeGroups[WW_GROUP] = [ftrMarketId];
          }
        }

        return {
          ...evnt,
          markets: marketTypeGroups
        };
      })
    );
  });

  /**
   * Sorts the event by date order.
   * If an event is set to priority then it will display at start of list regardless of start time.
   * If the events have matching fixture start time, then the events are ordered by which contains a promo first.
   */
  const eventsOrderbyDate = computed(
    () =>
      eventsWithGroupedMarkets.value?.sort((a, b) => {
        // make the priority event first in the list
        if (a.priorityEvent) {
          return -1;
        }
        if (b.priorityEvent) {
          return 1;
        }

        if (new Date(a.fixture) < new Date(b.fixture)) {
          return -1;
        }
        if (new Date(a.fixture) > new Date(b.fixture)) {
          return 1;
        }

        if (
          a.markets[PROMO_GROUP]?.length === 1 &&
          !b.markets[PROMO_GROUP]?.length
        ) {
          return -1;
        }

        if (
          !a.markets[PROMO_GROUP]?.length &&
          b.markets[PROMO_GROUP]?.length === 1
        ) {
          return 1;
        }

        return 0;
      }) || []
  );

  type EventMarkets = Map<CardTypes, string[]>;

  /**
   * Ordering the markets for each of the ordered events
   */
  const orderedEvents = computed(() => {
    return eventsOrderbyDate.value.reduce((eventsAccumulator, currentEvent) => {
      // add events to map in the correct order
      const orderedEventsMarkets: EventMarkets = new Map();

      if (currentEvent.markets.promo)
        orderedEventsMarkets.set("promo", currentEvent.markets.promo);

      if (currentEvent.markets.mainMarket)
        orderedEventsMarkets.set("mainMarket", currentEvent.markets.mainMarket);

      if (currentEvent.markets.insight)
        orderedEventsMarkets.set("insight", currentEvent.markets.insight);

      if (currentEvent.markets.wdw)
        orderedEventsMarkets.set("wdw", currentEvent.markets.wdw);

      if (currentEvent.markets.ww)
        orderedEventsMarkets.set("ww", currentEvent.markets.ww);

      if (currentEvent.markets.list)
        orderedEventsMarkets.set("list", currentEvent.markets.list);

      return [...eventsAccumulator, orderedEventsMarkets];
    }, [] as EventMarkets[]);
  });

  /**
   * flatten orderedEvents map to an array of arrays where
   * first item in array is type of market
   * and second item is array or market id
   *
   * [
   *  ['promo', [...marketIds]],
   *  ['mainMarket', [...marketIds]],
   *  ['insight', [...marketIds]],
   *  ['wdw', [...marketIds]],
   *
   *  ...
   *
   * ]
   * */

  const flattendOrderedEvents = computed(() => {
    return orderedEvents.value.reduce((acc, cur) => {
      return [...acc, ...cur.entries()];
    }, [] as [string, Array<string>][]);
  });

  /**
   * Getting ids of the first 3 market cards displayed for analytics
   */
  const firstThreeMarketIds = computed(() => {
    return flattendOrderedEvents.value
      .map(market => {
        return market[1];
      })
      .flat(1)
      .slice(0, 3)
      .map(id => ({ betID: id }));
  });

  return {
    eventsOrderbyDate,
    orderedEvents,
    firstThreeMarketIds,
    parlayMarkets,
    flattendOrderedEvents
  };
}

export const DisplayEventsSymbol = Symbol("DisplayEventsSymbol");
export type DisplayEventsContext = ReturnType<typeof useDisplayEvents>;

export function provideDisplayEvents(betData: Ref<BetData>): void {
  provide(DisplayEventsSymbol, useDisplayEvents(betData));
}

export function injectDisplayEvents(): DisplayEventsContext | undefined {
  return inject<DisplayEventsContext>(DisplayEventsSymbol);
}
